var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Colors } from '@pacem/pacem-foundation';
import { Geometry } from '@pacem/pacem-numerical';
import { Utils, PCSS, Transformer, Animations } from '@pacem/pacem-core';
import { Components, Drawing3D } from '@pacem/pacem-3d';
const PHI = (1 + Math.sqrt(5)) / 2;
const DIV_PHI_R = 360 * (Math.sqrt(5) - 1) / 2;
const DIV_DEFAULT = 180 * (1 - 1 / PHI);
const DIV_PHI = DIV_DEFAULT * 2;
const Matrix3D = Geometry.LinearAlgebra.Matrix3D;
const Quaternion = Geometry.LinearAlgebra.Quaternion;
const Vector3D = Geometry.LinearAlgebra.Vector3D;
const StalePropertyFlag = Drawing3D.StalePropertyFlag;
const KnownShader = Drawing3D.KnownShader;
var _allColors;
function getColors() {
    return _allColors ??= ['#dd2020',
        '#1ebb8f',
        '#c2b010',
        '#116e8b',
        '#624472',
        '#429bbb',
        '#d46d00',
        '#ff0085',
        '#a6715e',
        '#808080',
        "rgb(81,145,105)", "rgb(32,142,183)", "rgb(110,215,216)", "rgb(33,77,78)", "rgb(13,243,143)", "rgb(173,228,124)", "rgb(121,157,16)", "rgb(118,240,20)", "rgb(33,167,8)", "rgb(31,65,150)", "rgb(200,126,248)", "rgb(130,61,177)", "rgb(175,198,254)", "rgb(127,123,201)", "rgb(129,32,80)", "rgb(222,12,163)", "rgb(251,133,182)", "rgb(90,62,79)", "rgb(179,45,249)", "rgb(249,207,127)", "rgb(124,68,14)"]
        .map(c => { return { flags: [], shader: KnownShader.Basic, color: Colors.parse(c) }; });
}
export function getHeliconeMetadata(playing) {
    return {
        display: { cssClass: [PCSS + '-grid', 'grid-nospace', 'form-minimal'] },
        props: [
            {
                prop: 'divergence', type: 'number', display: { name: 'divergence angle', cssClass: [PCSS + '-cell', 'cols-lg-5', 'field-stretch', 'field-outline'] },
                extra: { step: 1 }, commands: [
                    { dependsOnValue: true, name: 'divdefault', icon: 'clock_loader_10', tooltip: 'phi/2 divergence' },
                    { dependsOnValue: true, name: 'divphi', icon: 'clock_loader_40', tooltip: 'phi divergence' },
                    { dependsOnValue: true, name: 'divphir', icon: 'clock_loader_60', tooltip: '0.618 divergence' },
                ],
                validators: [
                    { type: 'required', errorMessage: 'required' },
                    { type: 'range', errorMessage: 'out of range', params: { min: 0, max: 360 } }
                ]
            },
            {
                prop: 'divergence', type: 'number', display: { name: ' ', cssClass: [PCSS + '-cell', 'cols-lg-7', 'field-stretch', 'hide-v', 'hide-xs', 'field-outline', 'field-minimal'], ui: 'slider' },
                extra: { step: 1 },
                validators: [
                    { type: 'required', errorMessage: 'required' },
                    { type: 'range', errorMessage: 'out of range', params: { min: 0, max: 360 } }
                ]
            },
            {
                prop: 'layers', type: 'int', display: { name: 'levels', cssClass: [PCSS + '-cell', 'field-stretch', 'field-outline', 'field-minimal'], ui: 'slider', description: 'Alternatively, **half** the number of leaves.' },
                extra: { tooltip: 'md' },
                validators: [
                    { type: 'required', errorMessage: 'required' },
                    { type: 'range', errorMessage: 'out of range', params: { min: 10, max: 1000 } }
                ]
            },
            {
                prop: 'colors', type: 'int', display: { name: 'colors', cssClass: [PCSS + '-cell', 'cols-lg-5', 'field-stretch', 'field-outline'] },
                extra: { step: 1 }, commands: [
                    { dependsOnValue: true, name: 'fibprev', icon: 'chevron_left', tooltip: 'fib previous' },
                    { dependsOnValue: true, name: 'fibnext', icon: 'chevron_right', tooltip: 'fib next' },
                ],
                validators: [
                    { type: 'required', errorMessage: 'required' },
                    { type: 'range', errorMessage: 'out of range', params: { min: 2, max: 21 } }
                ]
            },
            {
                prop: 'colors', type: 'number', display: { name: ' ', cssClass: [PCSS + '-cell', 'cols-lg-7', 'hide-xs', 'hide-v', 'field-stretch', 'field-outline', 'field-minimal'], ui: 'slider' },
                extra: { step: 1 },
                validators: [
                    { type: 'required', errorMessage: 'required' },
                    { type: 'range', errorMessage: 'out of range', params: { min: 2, max: 21 } }
                ]
            },
            {
                prop: 'core', type: 'boolean', display: { name: 'show core', cssClass: [PCSS + '-cell', 'cols-4', 'field-outline'], ui: 'switcher' }
            },
            {
                prop: 'crown', type: 'boolean', display: { name: 'show crown', cssClass: [PCSS + '-cell', 'cols-4', 'field-outline'], ui: 'switcher' }
            },
            {
                prop: 'transparent', type: 'boolean', display: { name: 'transparent background', cssClass: [PCSS + '-cell', 'cols-4', 'field-outline'], ui: 'switcher' }
            },
            {
                prop: 'perspective', type: 'boolean', dataType: 'enumeration', display: {
                    name: 'camera', cssClass: [PCSS + '-cell', 'field-outline', 'field-stretch']
                },
                extra: {
                    enum: [
                        { value: true, caption: 'perspective' },
                        { value: false, caption: 'orthographic' },
                    ]
                }
            }
        ]
    };
}
const CAMERA_ABOVE = {
    position: { x: 0, y: 5, z: 0 },
    up: { x: 0, y: 0, z: -1 },
    lookAt: Vector3D.zero()
};
const CAMERA_DEFAULT = {
    position: { x: 0, y: 2, z: 4 },
    up: { x: 0, y: 1, z: 0 },
    lookAt: Vector3D.zero()
};
export const Cameras = {
    TOP: CAMERA_ABOVE,
    DEFAULT: CAMERA_DEFAULT
};
export const DivergenceAngles = {
    DEFAULT: DIV_DEFAULT,
    PHI: DIV_PHI,
    PHI_R: DIV_PHI_R,
};
var _config;
function getDefaultConfiguration() {
    return _config ??= {
        colors: 5, core: false, crown: false, divergence: DIV_DEFAULT, height: 2, layers: 120, perspective: true, transparent: false
    };
}
class HeliconeTransformers {
    static getDefaultHeliconeConfiguration() {
        return getDefaultConfiguration();
    }
    static datasource(group, config = HeliconeTransformers.getDefaultHeliconeConfiguration()) {
        const { childRenderables, flags } = helicone(group.childRenderables, config);
        group.flags.push(...flags);
        return childRenderables;
    }
    static tweenCam(camera, { position, lookAt, up }) {
        const tweener = new Animations.TweenService();
        const { position: position0, lookAt: lookAt0, up: up0 } = camera;
        const deltaPositionX = position.x - position0.x;
        const deltaPositionY = position.y - position0.y;
        const deltaPositionZ = position.z - position0.z;
        const deltaUpX = up.x - up0.x;
        const deltaUpY = up.y - up0.y;
        const deltaUpZ = up.z - up0.z;
        const deltaLookAtX = lookAt.x - lookAt0.x;
        const deltaLookAtY = lookAt.y - lookAt0.y;
        const deltaLookAtZ = lookAt.z - lookAt0.z;
        return tweener.run(0.0, 1.0, 1500, 0, Animations.Easings.sineInOut, (t, value) => {
            camera.position = {
                x: position0.x + deltaPositionX * value,
                y: position0.y + deltaPositionY * value,
                z: position0.z + deltaPositionZ * value,
            };
            camera.up = {
                x: up0.x + deltaUpX * value,
                y: up0.y + deltaUpY * value,
                z: up0.z + deltaUpZ * value,
            };
            camera.lookAt = {
                x: lookAt0.x + deltaLookAtX * value,
                y: lookAt0.y + deltaLookAtY * value,
                z: lookAt0.z + deltaLookAtZ * value,
            };
        });
    }
}
__decorate([
    Transformer('demo.helicone.default')
], HeliconeTransformers, "getDefaultHeliconeConfiguration", null);
__decorate([
    Transformer('demo.helicone.')
], HeliconeTransformers, "datasource", null);
__decorate([
    Transformer('demo.')
], HeliconeTransformers, "tweenCam", null);
const STEP = .01;
const CONE_POSITIONING_FUNCTION = (height, y, radius = 1) => {
    return STEP + radius * (height - y) / height;
};
const CONE_SCALING_FUNCTION = (height, y, _ = 1) => {
    return .035 + .275 * Math.sin((height - y) / height);
};
function mergeMesh(a, b) {
    const flags = [];
    const tagA = a.tag, tagB = b.tag;
    if (a.hide !== b.hide) {
        flags.push(StalePropertyFlag.Visibility);
    }
    if (tagA.color !== tagB.color) {
        flags.push(StalePropertyFlag.Material);
    }
    if (tagA.y !== tagB.y) {
        flags.push(StalePropertyFlag.Position);
    }
    if (tagA.type !== tagB.type) {
        flags.push(StalePropertyFlag.Geometry);
    }
    if (tagA.theta !== tagB.theta || tagA.offset !== tagB.offset) {
        flags.push(StalePropertyFlag.Transform);
    }
    Utils.extend(a, b, { flags });
}
var _backend;
function getDefaultHeliconeBackend() {
    return _backend ??= {
        stick: Components.Drawing3D.PacemBoxElement.createMeshGeometry(1, .01, .01, 1, 1, 1),
        leaf: Components.Drawing3D.PacemSphereElement.createMeshGeometry(1, 48, true),
        palette: getColors()
    };
}
function helicone(items, entity, backend = getDefaultHeliconeBackend(), positioningFunction = CONE_POSITIONING_FUNCTION, scaleFunction = CONE_SCALING_FUNCTION) {
    const config = Utils.extend({}, backend, entity);
    const COLORS = config.palette;
    const height = config.height;
    const colors = config.colors;
    const divergenceAngleDeg = config.divergence;
    const step = height / config.layers;
    const initialLength = items.length;
    const retval = { childRenderables: items, flags: [] };
    for (let index = 0; index < config.layers; index++) {
        const j = index * step;
        const y = height - j;
        const radius = positioningFunction(height, y);
        const colorIndex = index % colors;
        const color = COLORS[colorIndex % COLORS.length];
        const theta = divergenceAngleDeg * index;
        const quaternion = Quaternion.fromAxisAngle(Vector3D.j(), theta);
        const rotationMatrix = Quaternion.toRotationMatrix(quaternion);
        const stickChildIndex = index * 3;
        const leaf1ChildIndex = stickChildIndex + 1;
        const leaf2ChildIndex = stickChildIndex + 2;
        const stick = {
            inert: true,
            hide: config.layers > 500,
            tag: { type: 'stick', y, offset: radius, theta, color: colorIndex },
            geometry: config.stick,
            material: color,
            position: { x: 0, y, z: 0 },
            transformMatrix: Matrix3D.multiply(Matrix3D.clone(Matrix3D.identity(), m => { m.m11 = 2 * radius; }), rotationMatrix), flags: []
        };
        const size = scaleFunction(height, y);
        const scaleMatrix = Matrix3D.scale(Matrix3D.identity(), size * .5);
        const leaf1 = {
            inert: true,
            tag: { type: 'leaf', y, offset: radius, theta, color: colorIndex },
            geometry: config.leaf,
            material: color,
            position: { x: 0, y, z: 0 }, transformMatrix: Matrix3D.multiply(Matrix3D.clone(scaleMatrix, m => { m.offsetX = radius; }), rotationMatrix), flags: []
        };
        const leaf2 = {
            inert: true,
            tag: { type: 'leaf', y, offset: -radius, theta, color: colorIndex },
            geometry: config.leaf,
            material: color,
            position: { x: 0, y, z: 0 }, transformMatrix: Matrix3D.multiply(Matrix3D.clone(scaleMatrix, m => { m.offsetX = -radius; }), rotationMatrix), flags: []
        };
        if (stickChildIndex >= initialLength) {
            items.push(stick);
        }
        else {
            mergeMesh(items[stickChildIndex], stick);
        }
        if (leaf1ChildIndex >= initialLength) {
            items.push(leaf1);
        }
        else {
            mergeMesh(items[leaf1ChildIndex], leaf1);
        }
        if (leaf2ChildIndex >= initialLength) {
            items.push(leaf2);
        }
        else {
            mergeMesh(items[leaf2ChildIndex], leaf2);
        }
    }
    const totalCount = config.layers * 3;
    if (totalCount != initialLength) {
        retval.flags.push(StalePropertyFlag.Children);
    }
    if (totalCount < initialLength) {
        items.splice(totalCount);
    }
    return retval;
}
